import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {Alert, App as AntdApp, Select} from "antd";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetTrendsQuery} from "~/reduxs/api/trendsApiSlice";
import {useAddKeywordItemsMutation} from "~/reduxs/api/keywordApiSlice";
import TrendsTable from "~/features/GoogleTrends/components/TrendsTable";
import TrendsKeywordModal from "~/features/GoogleTrends/components/TrendsKeywordModal";

/**
 * Tab "Xu hướng" trong tool Nghiên cứu từ khoá — tái dùng bảng trending của tool
 * Google Trends (`features/GoogleTrends`) nhưng LƯU THẲNG vào kho của dự án hiện
 * tại (không cần modal chọn dự án như tool độc lập /trends). API api/trends nhận
 * cả cap 'keyword' nên user chỉ có quyền Nghiên cứu từ khoá vẫn dùng được.
 */
function KeywordTrending({projectId}) {

	const {notification} = AntdApp.useApp();
	const navigate = useNavigate();

	const [geo, setGeo] = useState('VN');
	const {data, isFetching} = useGetTrendsQuery(geo);
	const [addItems, {isLoading: saving}] = useAddKeywordItemsMutation();

	// Modal biểu đồ tần suất trend (null = đóng).
	const [viewing, setViewing] = useState(null);

	const items = data?.items || [];
	const geoOptions = Object.entries(data?.geos || {VN: 'Việt Nam'}).map(([value, label]) => ({value, label}));

	const handleSave = async (rows) => {
		if (!rows?.length || saving) return;

		try {
			const res = await addItems({
				projectId,
				items: rows.map((r) => ({
					keyword: r.keyword,
					source: 'trends',
					meta: {
						traffic: r.approx_traffic || '',
						traffic_min: r.traffic_min || 0,
						geo,
						news_url: r.news?.[0]?.url || '',
					},
				})),
			}).unwrap();

			if (res?.added > 0) {
				notification.success({message: 'Đã lưu vào kho', description: `${res.added} từ khoá mới${res.skipped ? ` (${res.skipped} đã có sẵn)` : ''}.`});
			} else {
				notification.info({message: 'Đã có', description: 'Các từ khoá này đã có trong kho.'});
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được từ khoá')});
		}
	};

	const handleSuggest = (row) => {
		navigate('/ai', {
			state: {
				task: 'trend_content',
				input: {
					keyword: row.keyword,
					news: (row.news || []).map((n) => n.title).join('\n'),
				},
			},
		});
	};

	return (
		<div>
			<div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap'}}>
				<Select style={{minWidth: 160}} value={geo} onChange={setGeo} options={geoOptions} />
				{data?.fetched_at && (
					<span style={{color: '#9ca3af', fontSize: 12}}>Cập nhật lúc {data.fetched_at}</span>
				)}
			</div>

			{data?.stale && (
				<Alert
					type="warning" showIcon style={{marginBottom: 12}}
					message="Google tạm thời chặn yêu cầu — đang hiển thị dữ liệu gần nhất còn lưu."
				/>
			)}

			<TrendsTable
				items={items}
				loading={isFetching || saving}
				onSave={handleSave}
				onSuggest={handleSuggest}
				onShowHistory={(row) => setViewing(row)}
			/>

			<TrendsKeywordModal
				open={!!viewing}
				keyword={viewing?.keyword}
				hash={viewing?.hash}
				geo={geo}
				onCancel={() => setViewing(null)}
			/>
		</div>
	);
}

export default KeywordTrending;
